import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQueryParam, NumberParam, StringParam } from "use-query-params";
import { clientService } from "../../../../../../api";
import { Table, TableSkeleton, TopProgress, Typography } from "../../../../../../components";
import { timeConverterMMDDYYHHMM } from "../../../../../../utils/dates";
import { useTheme } from "styled-components";
import Skeleton from "react-loading-skeleton";
import { useUi } from "../../../../../../context/ui.context";
import { Container, LogLink } from "./styles";

const EmailLog: FC = () => {
  const [t] = useTranslation();
  const theme: any = useTheme();
  const { userId } = useParams();
  const { viewAlert } = useUi();

  const [page] = useQueryParam("page", NumberParam);
  const [ordering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);

  const [logs, setLogs] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<any>();
  const [pageLoading, setPageLoading] = useState<boolean>(true);

  const columns = useMemo(
    () => [
      {
        title: t("SUBJECT"),
        key: "subject",
      },
      {
        title: t("TO"),
        key: "to",
      },
      {
        title: t("ENTRY_DATE"),
        key: "created_at",
      },
    ],
    [t]
  );

  const fetchEmailLogs = () => {
    if (!userId) return;
    clientService
      .getEmailLogs({
        page: page || 1,
        pageSize: pageSize || 20,
        ordering: ordering || "-created_at",
        client: userId,
      })
      .then((res) => {
        setLogs(
          res.data.objects.map((item: any) => ({
            ...item,
            subject: (
              <LogLink to={`/utilities/email-message-log/${item.id}`}>
                {item.subject || "-"}
              </LogLink>
            ),
            to: (
              <LogLink to={`/utilities/email-message-log/${item.id}`}>
                {item.recipients || item.to || "-"}
              </LogLink>
            ),
            created_at: (
              <LogLink to={`/utilities/email-message-log/${item.id}`}>
                {timeConverterMMDDYYHHMM(item.created_at)}
              </LogLink>
            ),
          }))
        );
        setPaginationData(res.data);
      })
      .catch((e: any) => {
        if (e?.response?.data?.detail) {
          viewAlert({
            severity: "error",
            message: e?.response?.data?.detail,
          });
        }
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  useEffect(() => {
    setPageLoading(true);
    fetchEmailLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, ordering, userId]);

  if (pageLoading) {
    return (
      <Container>
        <TopProgress loading />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width={160}
          height={30}
          style={{ marginBottom: "12px" }}
        />
        <TableSkeleton rows={6} columns={columns.length} />
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h2" customStyles={{ marginBottom: "12px" }}>
        {t("EMAIL_LOG")}
      </Typography>
      <Table
        columns={columns}
        rows={logs}
        hideCheckbox
        paginationData={paginationData}
      />
    </Container>
  );
};

export default EmailLog;
